/**
 * Layout Protection Utility
 * Prevents layout shifts during PDF generation and other operations
 */

export class LayoutProtector {
  private originalBodyStyles: { [key: string]: string } = {};
  private originalOverflow: string = '';
  private isProtected: boolean = false;

  /**
   * Protect layout from shifts during operations
   */
  protect(): void {
    if (this.isProtected) return;

    // Store original body styles
    this.originalOverflow = document.body.style.overflow;
    this.originalBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      width: document.body.style.width,
      height: document.body.style.height
    };

    // Apply minimal layout protection - only prevent scrolling
    // Don't set position: fixed as it causes layout shifts
    document.body.style.overflow = 'hidden';

    this.isProtected = true;
    console.log('Layout protection activated (minimal)');
  }

  /**
   * Restore original layout
   */
  restore(): void {
    if (!this.isProtected) return;

    // Restore original overflow style only
    document.body.style.overflow = this.originalOverflow;

    this.isProtected = false;
    console.log('Layout protection deactivated (minimal)');
  }

  /**
   * Check if layout is currently protected
   */
  getProtected(): boolean {
    return this.isProtected;
  }
}

/**
 * Global layout protector instance
 */
export const globalLayoutProtector = new LayoutProtector();

/**
 * Hook to use layout protection in components
 */
export const useLayoutProtection = () => {
  const protect = () => globalLayoutProtector.protect();
  const restore = () => globalLayoutProtector.restore();
  const isProtected = () => globalLayoutProtector.getProtected();

  return { protect, restore, isProtected };
};

/**
 * Higher-order function to wrap operations with layout protection
 */
export const withLayoutProtection = async <T>(
  operation: () => Promise<T>
): Promise<T> => {
  try {
    globalLayoutProtector.protect();
    const result = await operation();
    return result;
  } finally {
    globalLayoutProtector.restore();
  }
}; 